import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './BurgerNav.styles';
import { BurgerNavDrawerProps } from './BurgerNavDrawer.types';
import { getNavItemsByOrder } from './BurgerNavDrawer.helpers';
import { BURGER_NAV_ITEMS_ORDER } from './BurgerNavDrawer.constants';
import { useAnimatedVisibility } from '@/hooks/useAnimatedVisibility';
import { useBridgeState } from '@/bridge/hooks';
import { useChangeLang } from '@/components/ChangeLang/useChangeLang';
import { useTheme } from '@/hooks/theme/useTheme';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { ChangeLang } from '@/components/ChangeLang/ChangeLang';
import { makeHrefForPhone } from '@/utils/helpers';
import { CONTACT_US } from '@/constants/contactUs';

export const BurgerNavDrawer = ({
  isOpen,
  toggleNav,
}: BurgerNavDrawerProps) => {
  const { isVisible, onAnimationEnd } = useAnimatedVisibility(isOpen);
  const { isWebView } = useBridgeState();
  const changeLang = useChangeLang();
  const { t } = useTranslation();
  const { isDarkTheme } = useTheme();

  if (!isVisible) return null;

  return (
    <S.BurgerNavWrapper
      data-testid={CommonTestIds.navMenu.drawer}
      $isOpen={isOpen}
      onAnimationEnd={onAnimationEnd}
    >
      <S.BurgerNavigation>
        <S.BurgerNavItems $isTop>
          {getNavItemsByOrder(BURGER_NAV_ITEMS_ORDER, toggleNav).map(
            ({ id, ...item }) => (
              <S.BurgerNavItem key={id} {...item} />
            )
          )}
        </S.BurgerNavItems>

        <S.BurgerNavItems $gap={20} $isFooter>
          {!isWebView && (
            <S.BurgerNavItem
              onClick={changeLang}
              $leftIcon={{
                name: 'language.svg',
                width: 19,
                height: 19,
                margin: '0.5px 10.5px 0.5px 0.5px',
              }}
            >
              <ChangeLang />
            </S.BurgerNavItem>
          )}
          <S.BurgerNavItems>
            <S.ConnectWithUsTitle>
              {t('common.connect_with_us')}
            </S.ConnectWithUsTitle>
            <S.BurgerNavItem
              data-testid={CommonTestIds.navMenu.phone}
              href={makeHrefForPhone(CONTACT_US.PHONE)}
              as="a"
              $leftIcon={{
                name: `phone${isDarkTheme ? '-light' : ''}.svg`,
                width: 15,
                height: 15,
                margin: '2.5px',
              }}
              $isContactUsItem
            >
              {CONTACT_US.PHONE}
            </S.BurgerNavItem>
          </S.BurgerNavItems>
          <S.BurgerNavItem
            data-testid={CommonTestIds.navMenu.email}
            href={`mailto:${CONTACT_US.EMAIL}`}
            as="a"
            target="_blank"
            $isContactUsItem
          >
            {CONTACT_US.EMAIL}
          </S.BurgerNavItem>
          <S.BurgerNavItems $isRow $gap={19}>
            <S.BurgerNavItem
              data-testid={CommonTestIds.navMenu.facebook}
              href={CONTACT_US.FACEBOOK}
              as="a"
              target="_blank"
              $leftIcon={{ name: `fb_grey.svg` }}
              $isContactUsItem
            />
            <S.BurgerNavItem
              data-testid={CommonTestIds.navMenu.instagram}
              href={CONTACT_US.INSTAGRAM}
              as="a"
              target="_blank"
              $leftIcon={{ name: `instagram-alt.svg` }}
              $isContactUsItem
            />
          </S.BurgerNavItems>
        </S.BurgerNavItems>
      </S.BurgerNavigation>
    </S.BurgerNavWrapper>
  );
};
